import React, { useState, useEffect } from 'react';
import * as Icons from './Icons';
import { API_URL } from '../api';

// Simple loadBabel utility
const loadBabel = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    if ((window as any).Babel) {
      resolve((window as any).Babel);
      return;
    }
    const existingScript = document.getElementById('babel-standalone-script');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve((window as any).Babel));
      existingScript.addEventListener('error', () => reject(new Error('Babel script load error')));
      return;
    }

    const script = document.createElement('script');
    script.id = 'babel-standalone-script';
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/babel-standalone/7.24.0/babel.min.js';
    script.async = true;
    script.onload = () => {
      if ((window as any).Babel) {
        resolve((window as any).Babel);
      } else {
        reject(new Error('Babel failed to initialize after loading'));
      }
    };
    script.onerror = () => reject(new Error('Babel script failed to load'));
    document.head.appendChild(script);
  });
};

// React Error Boundary to capture runtime failures inside the sandbox
class SandboxErrorBoundary extends React.Component<
  { children: React.ReactNode; onError: (error: Error, info: any) => void },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    this.props.onError(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return null; // Parent handles rendering the beautiful error view
    }
    return this.props.children;
  }
}

interface MasterHandSandboxProps {
  componentId: string;
  code: string;
  adminUsername: string;
  onCompileSuccess?: () => void;
  onCompileError?: (error: string) => void;
}

export const MasterHandSandbox = ({
  componentId,
  code,
  adminUsername,
  onCompileSuccess,
  onCompileError
}: MasterHandSandboxProps) => {
  const [Babel, setBabel] = useState<any>(null);
  const [transpiling, setTranspiling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [CompiledComponent, setCompiledComponent] = useState<React.ComponentType<any> | null>(null);

  // Load Babel Standalone on mount
  useEffect(() => {
    loadBabel()
      .then((b) => {
        setBabel(b);
      })
      .catch((err) => {
        setError(`Failed to load compiler engine: ${err.message}`);
        onCompileError?.(err.message);
      });
  }, []);

  // Re-compile whenever code changes
  useEffect(() => {
    if (!Babel || !code) return;

    setTranspiling(true);
    setError(null);

    const timer = setTimeout(() => {
      try {
        // 1. Transpile TSX/JSX down to ES5 with Babel
        const result = Babel.transform(code, {
          presets: ['react', 'typescript'],
          filename: `${componentId}.tsx`
        });

        const compiledJS = result.code;

        // 2. Setup module mock system
        const exports: any = {};
        const mockRequire = (moduleName: string) => {
          const normalized = moduleName.toLowerCase();
          
          if (normalized === 'react') {
            return React;
          }
          if (normalized.endsWith('icons') || normalized.includes('/icons')) {
            return Icons;
          }
          if (normalized.endsWith('api') || normalized.includes('/api')) {
            return { API_URL };
          }
          if (normalized.endsWith('types') || normalized.includes('/types')) {
            return {}; // Stub out TypeScript types
          }
          if (normalized.includes('usescrolllock')) {
            return { useScrollLock: () => {} };
          }
          if (normalized.includes('postgrid')) {
            return {
              PostGrid: () => (
                <div className="p-6 bg-white/5 rounded-3xl border border-white/10 text-center text-xs tracking-wider uppercase font-black text-gray-500 italic">
                  [PostGrid Preview Stub]
                </div>
              )
            };
          }
          if (normalized.includes('postcreator')) {
            return { FootprintPostCreatorModal: () => null };
          }
          if (normalized.includes('leaderboards')) {
            return {
              Leaderboards: () => (
                <div className="p-6 bg-white/5 rounded-3xl border border-white/10 text-center text-xs tracking-wider uppercase font-black text-yellow-500/80 italic">
                  [Leaderboard Preview Stub]
                </div>
              )
            };
          }
          if (normalized.includes('modregistry')) {
            return {
              getActiveMods: () => [],
              getAllMods: () => []
            };
          }
          if (normalized.includes('constants')) {
            return {
              STORE_ITEMS: [],
              CATEGORIES: []
            };
          }

          throw new Error(`Import error: Module "${moduleName}" not registered in Master Hand Sandbox.`);
        };

        // 3. Evaluate code
        const evalFn = new Function('exports', 'require', 'React', compiledJS);
        evalFn(exports, mockRequire, React);

        // 4. Resolve exported Component
        const resolvedComponent =
          exports.default ||
          exports.TodoMod ||
          exports.FootprintPage ||
          exports.PimpMyRide ||
          exports.ArtClass ||
          Object.values(exports).find((val) => typeof val === 'function');

        if (!resolvedComponent) {
          throw new Error(`Could not find a valid React component exported in ${componentId}.tsx`);
        }

        setCompiledComponent(() => resolvedComponent);
        setError(null);
        onCompileSuccess?.();
      } catch (err: any) {
        console.error('Master Hand Transpilation Error:', err);
        setError(err.message || 'Unknown compilation error');
        onCompileError?.(err.message || 'Unknown compilation error');
      } finally {
        setTranspiling(false);
      }
    }, 400); // Debounce to allow clean typing experience

    return () => clearTimeout(timer);
  }, [Babel, code, componentId]);

  // Generate realistic default mockup props depending on componentId
  const getMockProps = () => {
    switch (componentId) {
      case 'todo':
        return {
          username: adminUsername,
          themeColor: '#8b5cf6'
        };
      case 'vibeman1_footprint':
        return {
          posts: [],
          onPostClick: () => {},
          onLike: () => {},
          onBump: () => {},
          onBookmark: () => {},
          onEdit: () => {},
          onDelete: () => {},
          onNavigateToHashtag: () => {},
          animatingLikes: new Set(),
          animatingBumps: new Set(),
          animatingBookmarks: new Set(),
          galleryAnimation: '',
          postBlurOpacity: 100,
          currentUserData: {
            username: adminUsername,
            totalPoints: 1337,
            streak: 5,
            completedAchievements: []
          },
          currentUserId: adminUsername,
          onCompleteAchievement: () => {},
          allUsers: {},
          dailyAchievements: {
            daily: {
              id: 1,
              title: 'Master Sandbox Task',
              description: 'Successfully complete a sandboxed execution preview of Master Hand.',
              points: 250
            }
          },
          onOpenAdminSettings: () => {},
          isAdmin: true,
          onNavigateToProfile: () => {},
          setPage: () => {},
          achievements: [],
          isDemo: true
        };
      case 'pimp_my_ride':
        return {
          currentUser: adminUsername,
          usersData: {
            [adminUsername]: {
              totalPoints: 2450,
              purchasedItems: [],
              activeCustomizations: { mods: [] }
            }
          },
          updateUserData: () => {},
          appThemeColor: '#8b5cf6',
          onNavigate: () => {},
          isDemo: true
        };
      case 'art_class':
        return {
          username: adminUsername,
          isAdmin: true
        };
      default:
        return {};
    }
  };

  const handleRuntimeError = (err: Error) => {
    setError(`Runtime Render Error: ${err.message}`);
    onCompileError?.(`Runtime Render Error: ${err.message}`);
  };

  // Rendering
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center p-4 min-h-[300px]">
      {/* Dynamic Background Glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-600/5 rounded-3xl pointer-events-none" />

      {/* Loading Babel */}
      {!Babel && !error && (
        <div className="flex flex-col items-center gap-4 animate-pulse">
          <Icons.RotateIcon className="w-8 h-8 text-blue-500 animate-spin" />
          <span className="text-xs uppercase font-black tracking-widest text-gray-500 italic">
            Initializing Sandbox Engine...
          </span>
        </div>
      )}

      {/* Transpiling state indication */}
      {transpiling && (
        <div className="absolute top-4 right-4 z-50 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-md">
          <Icons.RotateIcon className="w-3.5 h-3.5 text-blue-400 animate-spin" />
          <span className="text-[9px] font-black uppercase tracking-widest text-blue-400">
            Transpiling...
          </span>
        </div>
      )}

      {/* Error state display (glassmorphic terminal styling) */}
      {error && (
        <div className="relative w-full max-w-lg p-6 rounded-3xl bg-red-500/10 border border-red-500/20 backdrop-blur-xl shadow-2xl animate-fade-in text-left">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-3 h-3 rounded-full bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.5)]" />
            <span className="text-xs font-black uppercase tracking-widest text-red-400 italic">
              Sandbox Compile Failure
            </span>
          </div>
          <pre className="text-xs font-mono text-red-200/90 whitespace-pre-wrap break-all bg-black/40 p-4 rounded-xl border border-red-500/10 overflow-y-auto max-h-[250px]">
            {error}
          </pre>
        </div>
      )}

      {/* Successfully rendered sandbox component */}
      {!error && CompiledComponent && (
        <div className="w-full h-full overflow-y-auto max-h-full rounded-2xl flex flex-col justify-start">
          <SandboxErrorBoundary key={code} onError={handleRuntimeError}>
            <CompiledComponent {...getMockProps()} />
          </SandboxErrorBoundary>
        </div>
      )}
    </div>
  );
};
